import * as z from "zod";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField } from "../../../providers/form";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import ComboBox from "widgets/shared/inputs/combobox";
import LabeledField from "providers/labeledField";
import CircleButton from "widgets/controls/buttons/circle-button";
import { MdDelete } from "react-icons/md";
import Checkbox from "widgets/shared/inputs/checkbox";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";
import React, { useEffect, useState, useRef } from "react";
import { SearchInput } from "widgets/shared/inputs/search";
import { userSearch } from "api/user";
import { Recipients } from "api/types/device";
import { notifySimple } from "providers/toast";
import GenericButton from "widgets/controls/buttons/generic";
import useWhiteLabel from "whitelabel/useWhiteLabel";
import BounceLoader from "react-spinners/ClipLoader";

// Schema definition for the form
const RecipientsSchema = z.object({
  recipients: z.array(
    z.object({
      name: z.string().optional(),
      email: z.string().optional(),
      phone: z.string().optional(),
      missing: z.string().optional(),
      username: z.string().optional(),
      platform: z.string().optional(),
      levels: z.number().optional().default(99),
    })
  ),
});

export type RecipientsFormType = z.infer<typeof RecipientsSchema>;

// Props for the form
type RecipientsFormProps = {
  submit: (data: RecipientsFormType) => void;
  onClose: () => void;
  defaultValues?: RecipientsFormType;
  values?: RecipientsFormType;
  loading?: boolean;
  disabled?: boolean;
  noLevels?: number;
  readonly?: boolean;
};

const RecipientsForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
  disabled,
  noLevels,
  readonly,
}: RecipientsFormProps) => {
  const { t } = useTranslation();
  const { platform } = useWhiteLabel();

  const form = useForm<z.infer<typeof RecipientsSchema>>({
    resolver: zodResolver(RecipientsSchema),
    defaultValues: values ?? { recipients: [] },
  });

  // Add useEffect to reset form when values changes
  useEffect(() => {
    if (values) {
      form.reset(values);
    }
  }, [values, form]);

  const [searchResults, setSearchResults] = useState<any[]>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { control, handleSubmit } = form;
  const [searchLoading, setSearchLoading] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "recipients",
  });

  // Generate dropdown items based on noLevels
  const generateLevelsDropdown = () => {
    const items = [
      {
        label: "App Only",
        value: 99,
      },
    ];

    if (noLevels && noLevels > 0) {
      for (let i = 1; i <= noLevels; i++) {
        items.push({
          label: `Level ${i}`,
          value: i,
        });
      }
    }

    return items;
  };

  // Automatically submit the form if valid
  const onSubmit = async (formData: RecipientsFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const searchUser = async (searchTerm: string) => {
    setSearchResults(null);
    if (searchTerm && searchTerm.length > 0) {
      setSearchLoading(true);
      const { data, isLoading } = await userSearch(searchTerm);
      if (data && data.length > 0) {
        // Get current recipient emails
        const currentRecipients = form.getValues("recipients") || [];
        const currentEmails = currentRecipients.map((r) => r.email);
        // Filter out users already in recipients
        const filtered = data.filter(
          (user: Recipients) => !currentEmails.includes(user.email)
        );
        setSearchResults(filtered);
        if (filtered.length === 0) {
          notifySimple("No users found", "info");
        }
      } else {
        notifySimple("No users found", "info");
      }

      setSearchLoading(isLoading);
    }
  };

  // Open dropdown when searchResults change
  useEffect(() => {
    setDropdownOpen(!!(searchResults && searchResults.length > 0));
  }, [searchResults]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <>
      {searchLoading && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <BounceLoader loading={searchLoading} />
        </div>
      )}
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
          {/* Rate type selector */}
          {!readonly && (
            <div className="relative mb-8 flex w-full" ref={dropdownRef}>
              <SearchInput value={undefined} onChange={searchUser} />
              {dropdownOpen && (
                <div className="absolute z-10 mt-2 max-h-48 w-full overflow-y-auto rounded border bg-white shadow">
                  {searchResults.map((user, idx) => (
                    <div
                      key={user.email + idx}
                      className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                      onClick={() => {
                        append({
                          name: `${user.name} ${user.familyName}`,
                          email: user.email,
                          phone: user.phoneAttr,
                          missing: "",
                          username: user.cognitoUsername,
                          platform: platform,
                          levels: 99,
                        });
                        setSearchResults(null);
                        setDropdownOpen(false);
                      }}
                    >
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                      {user.phone && (
                        <div className="text-xs text-gray-400">
                          {user.phone}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Recipients */}

          <div className="mb-2 grid grid-cols-5 gap-2">
            <LabeledField
              className="text-txt-lg"
              label={"NAME:"}
              id={""}
              children={""}
              textColor="secondary"
            />
            <LabeledField
              className="text-txt-lg"
              label={"EMAIL:"}
              id={""}
              children={""}
              textColor="secondary"
            />
            <LabeledField
              className="text-txt-lg"
              label={"PHONE:"}
              id={""}
              children={""}
              textColor="secondary"
            />
            <LabeledField
              className="text-txt-lg"
              label={"LEVELS:"}
              id={""}
              children={""}
              textColor="secondary"
            />
            {!readonly && (
              <LabeledField
                className="text-txt-lg"
                label={"MISSING:"}
                id={""}
                children={""}
                textColor="secondary"
              />
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {fields.length === 0 ? (
              <div className="py-4 text-center text-gray-500">
                No recipients
              </div>
            ) : (
              fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-5 gap-2 pb-2">
                  <FormField
                    control={control}
                    name={`recipients.${index}.name`}
                    render={({ field }) => (
                      <InputField
                        sizes={"md"}
                        placeholder={t("form.placeholder.name")}
                        id={`recipients.${index}.name`}
                        type="text"
                        disabled={true}
                        variant="solid"
                        color="secondary"
                        {...field}
                        extra="bg-green-50"
                      />
                    )}
                  />
                  <FormField
                    control={control}
                    name={`recipients.${index}.email`}
                    render={({ field }) => (
                      <InputField
                        sizes={"md"}
                        placeholder={t("form.placeholder.email")}
                        id={`recipients.${index}.email`}
                        type="text"
                        disabled={true}
                        variant="solid"
                        color="secondary"
                        {...field}
                        extra="bg-green-50"
                      />
                    )}
                  />
                  <FormField
                    control={control}
                    name={`recipients.${index}.phone`}
                    render={({ field }) => (
                      <InputField
                        sizes={"md"}
                        placeholder={t("form.placeholder.phone")}
                        id={`recipients.${index}.phone`}
                        type="text"
                        disabled={true}
                        variant="solid"
                        color="secondary"
                        {...field}
                        extra="bg-green-50"
                      />
                    )}
                  />
                  <FormField
                    control={control}
                    name={`recipients.${index}.levels`}
                    render={({ field }) => (
                      <ComboBox
                        sizes={"md"}
                        placeholder={t("form.placeholder.levels")}
                        id={`recipients.${index}.levels`}
                        variant="solid"
                        color="secondary"
                        extra="bg-green-50"
                        items={generateLevelsDropdown()}
                        labelKey="label"
                        valueKey="value"
                        type="number"
                        disabled={disabled || readonly}
                        clearIcon={false}
                        {...field}
                      />
                    )}
                  />
                  {!readonly && (
                    <div className="flex">
                      <FormField
                        control={control}
                        name={`recipients.${index}.missing`}
                        render={({ field }) => (
                          <Checkbox
                            color="secondary"
                            id={`recipients.${index}.missing`}
                            returnBoolean
                            disabled={readonly}
                            checked={field.value === "Yes"}
                            onChange={(checked: boolean) => {
                              form.setValue(
                                `recipients.${index}.missing`,
                                checked ? "Yes" : "No"
                              );
                            }}
                          />
                        )}
                      />
                      <CircleButton
                        color="primary"
                        icon={MdDelete}
                        onClick={() => remove(index)}
                        disabled={disabled || readonly}
                        size="sm"
                        variant="ghost"
                      />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end py-2">
            <div className="flex gap-2">
              {!readonly ? (
                <>
                  <GenericButton
                    onClick={onClose}
                    text={t("button.action.cancel")}
                    size="md"
                    variant="ghost"
                    color="secondary"
                  />
                  <GenericButton
                    type="submit"
                    text={t("button.action.ok")}
                    // disabled={!isDirty}
                    loading={loading || false}
                    size="md"
                  />{" "}
                </>
              ) : (
                <GenericButton
                  text={t("button.action.ok")}
                  // disabled={!isDirty}
                  loading={loading || false}
                  size="md"
                  onClick={onClose}
                />
              )}
            </div>
          </div>
        </form>
      </Form>
    </>
  );
};

export default RecipientsForm;
